import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';

const API_BASE = '/api/representativetypes';
const EMPTY_FORM = { code: '', name: '' };

function toNullableInt(value) {
  if (value === '' || value == null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function RepresentativeTypesPageLegacy() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const [sortField, setSortField] = useState('Code');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const raw = filterText.trim();
        const q = raw.replace(/'/g, "''").toLowerCase();
        const numeric = Number.parseInt(raw, 10);
        filterParams = `(contains(tolower(Name),'${q}')`;
        if (!Number.isNaN(numeric)) filterParams += ` or Code eq ${numeric}`;
        filterParams += ')';
      }

      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({ data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => {
    setFilterText(filterInput);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterInput('');
    setCurrentPage(1);
  };

  const rowToForm = (row) => ({
    code: row.code ?? '',
    name: row.name || '',
  });

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSelected(null);
    setModal('new');
  };

  const openEdit = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('edit');
  };

  const openView = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('view');
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('error', 'Temsilci tipi adi bos olamaz');
      return;
    }

    const payload = {
      code: toNullableInt(form.code),
      name: form.name,
    };

    try {
      const isNew = modal === 'new';
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id: selected.id, ...payload }),
      });
      if (!res.ok) throw new Error(isNew ? 'Kayit eklenemedi' : 'Kayit guncellenemedi');
      showToast('success', isNew ? 'Temsilci tipi basariyla eklendi' : 'Temsilci tipi basariyla guncellendi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'Temsilci tipi basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const columns = [
    { field: 'code', headerName: 'Kod', width: 130 },
    { field: 'name', headerName: 'Ad', flex: 1, minWidth: 220, renderCell: (params) => params.row.name || '-' },
    {
      field: 'createdAt',
      headerName: 'Olusturma Tarihi',
      width: 170,
      renderCell: (params) => params.row.createdAt ? new Date(params.row.createdAt).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'updatedAt',
      headerName: 'Guncelleme Tarihi',
      width: 180,
      renderCell: (params) => params.row.updatedAt ? new Date(params.row.updatedAt).toLocaleDateString('tr-TR') : '-',
    },
    {
      field: 'actions',
      headerName: 'Islemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Goruntule</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Degistir</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}>Sil</button>
        </div>
      ),
    },
  ];

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Temsilci Tipi Yonetimi</h2>
          <p style={s.pageSubtitle}>Temsilci tipi kayitlarini filtreleyin, siralayin ve yonetin.</p>
        </div>
        <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Temsilci Tipi</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Kod veya ad ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') handleFilterSearch(); }}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'Code'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Temsilci Tipi' : modal === 'edit' ? 'Temsilci Tipi Duzenle' : 'Temsilci Tipi Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod</label>
                  <input
                    style={inputStyle}
                    value={form.code}
                    onChange={e => setForm(current => ({ ...current, code: e.target.value }))}
                    readOnly={isView}
                    placeholder="Kod giriniz"
                    autoFocus
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ad *</label>
                  <input
                    style={inputStyle}
                    value={form.name}
                    onChange={e => setForm(current => ({ ...current, name: e.target.value }))}
                    readOnly={isView}
                    placeholder="Ad giriniz"
                  />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.name}"</strong> temsilci tipini silmek istediginize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
